import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const DISMISS_KEY = 'bpu_update_dismissed_until';

export const BpuUpdateBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bordereau/master-status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role === 'artisan') return;
    // Check dismissal locale (re-affichée toutes les 24h)
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (until && Date.now() < until) {
        setDismissed(true);
        return;
      }
    } catch { /* noop */ }
    checkStatus();
  }, [user, checkStatus]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      // Re-snooze pour 24h
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch { /* noop */ }
  };

  const goToBordereau = () => {
    navigate('/app/bordereau');
  };

  if (!user || user.role === 'artisan') return null;
  if (dismissed) return null;
  if (!status?.has_update) return null;
  // Cacher le bandeau quand on est déjà sur la page Bordereau
  if (location.pathname?.startsWith('/app/bordereau')) return null;

  return (
    <div
      className="bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-amber-500/15 border border-amber-500/40 rounded-lg px-4 py-2.5 flex items-center gap-3 shadow-lg shadow-amber-500/5 mb-4"
      data-testid="bpu-update-banner"
    >
      <div className="w-8 h-8 rounded-full bg-amber-500/25 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4 h-4 text-amber-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-amber-100 font-semibold leading-tight">
          🆕 Nouvelle version du BPU disponible
          {status.missing_lines > 0 && (
            <span className="text-amber-300 font-mono ml-1.5">+{status.missing_lines} ligne{status.missing_lines > 1 ? 's' : ''}</span>
          )}
        </p>
        <p className="text-[11px] text-amber-200/70 leading-tight mt-0.5">
          Vos prix personnalisés ne seront pas écrasés.
        </p>
      </div>
      <Button
        size="sm"
        className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold h-8 flex-shrink-0"
        onClick={goToBordereau}
        data-testid="bpu-banner-cta"
      >
        Voir <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
      <button
        type="button"
        onClick={handleDismiss}
        className="w-6 h-6 rounded text-amber-300/60 hover:text-amber-200 hover:bg-amber-500/20 flex items-center justify-center flex-shrink-0 transition-colors"
        title="Masquer pour 24h"
        data-testid="bpu-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default BpuUpdateBanner;
